import type { StudyState } from '@sigora/domain';

/**
 * The four things that happen to a launched study, in the words a researcher would use.
 *
 * Four fixed phases rather than the lifecycle itself, which is a graph: `live` can end in three
 * different states and two of them are endings nobody asked for, so a strip drawn from the states
 * would either have to branch on screen or pretend the branches do not exist. These are the coarse
 * stages every launched study passes through in the same order, which is the part that can be
 * drawn as a line without lying about it.
 *
 * The step counter and this strip answer different questions on purpose: the counter is honest only
 * during set-up, where there really is a fixed list of eight things being worked through, and this
 * is what replaces it for everything after — no numbers, because nothing past set-up has a
 * countable list of steps behind it.
 */
const PHASES = [
  { slug: 'setting-up', label: 'Setting up' },
  { slug: 'fielding', label: 'Fielding' },
  { slug: 'analyzing', label: 'Analyzing' },
  { slug: 'finished', label: 'Finished' },
] as const;

export type PhaseStatus = 'done' | 'current' | 'upcoming';

export interface PhasePosition {
  /** Which phase the study is at, as an index into the four. */
  index: number;
  /** Whether that phase is the last thing that will happen, so nothing is in progress. */
  settled: boolean;
}

export interface PhaseView {
  slug: string;
  label: string;
  status: PhaseStatus;
}

/**
 * How far a study has got, and whether anything is still in progress.
 *
 * `settled` is the whole reason this answers a pair rather than an index: a phase marked done with
 * nothing after it current is how the strip says "this study is not in a phase any more". Three
 * states need that, for two different reasons.
 *
 * `complete` is the end of the journey. `Finished` is a state the study is in rather than work
 * being done, so presenting it as the current phase would leave the strip permanently claiming
 * something is under way on a study where nothing will happen again.
 *
 * `under_recruited` and `cancelled` are endings too, and neither reached `Finished` — so the strip
 * has to be able to stop short. Presenting either as a phase in progress would claim something is
 * still happening, and presenting them as `Finished` would claim the study ended the way it was
 * meant to.
 *
 * Both of those are placed at `Fielding` rather than being told apart. `under_recruited` is
 * reachable from `live` and from `analyzing`, and the strip derives from the state alone, which
 * does not say which. Fielding is the phase both are known to have reached, and the sentence
 * beside the strip is what says how the study ended.
 *
 * A `live` study is at `Fielding` whatever its count: it is at `Analyzing` once the server
 * has moved it on to `analyzing`, and not before.
 */
export const phasePosition = ({
  state,
  settingUp,
}: {
  state: StudyState | null;
  /** Paid for and not yet fielding, as the launch panel derives it. */
  settingUp: boolean;
}): PhasePosition | null => {
  if (settingUp) {
    return { index: 0, settled: false };
  }

  switch (state) {
    case 'live':
      return { index: 1, settled: false };
    case 'analyzing':
      return { index: 2, settled: false };
    case 'complete':
      return { index: 3, settled: true };
    case 'under_recruited':
    case 'cancelled':
      return { index: 1, settled: true };
    default:
      // A draft, or a study that is ready and unpaid: nothing has happened to it yet, and a strip
      // over it would announce a journey the researcher has not agreed to start.
      return null;
  }
};

/** How each phase stands, given where the study is. */
const statusOf = (position: PhasePosition, index: number): PhaseStatus => {
  if (index < position.index) {
    return 'done';
  }

  if (index > position.index) {
    return 'upcoming';
  }

  return position.settled ? 'done' : 'current';
};

export const describePhases = (position: PhasePosition): PhaseView[] =>
  PHASES.map(({ slug, label }, index) => ({ slug, label, status: statusOf(position, index) }));

/**
 * Where the study is, as a strip above whatever the panel says next.
 *
 * It renders nothing at all for a study that has not been paid for, rather than a strip with the
 * first phase pending: the panel below it is a price and a button at that point, and a stage
 * display over an action reads as progress towards something that has not been started.
 */
export const PhaseStrip = ({
  state,
  settingUp,
}: {
  state: StudyState | null;
  settingUp: boolean;
}) => {
  const position = phasePosition({ state, settingUp });

  if (position === null) {
    return null;
  }

  return (
    <ol aria-label="Study progress" className="launch-phases" data-test-id="launch-phases">
      {describePhases(position).map(({ slug, label, status }) => (
        <li
          aria-current={status === 'current' ? 'step' : undefined}
          className={`launch-phase launch-phase-${status}`}
          data-phase-status={status}
          data-test-id={`launch-phase-${slug}`}
          key={slug}
        >
          {label}
        </li>
      ))}
    </ol>
  );
};
